import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import PageBaseProps from 'types/PageBase';
import { ConnectionTypeLogin, SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';

import { b } from 'utils/i18nUtils';
import { validatePlateNumber } from 'utils/formUtils';
import { useDispatch, useSelector } from 'hooks';

import { setLoginData } from 'modules/auth/actions';
import { login } from 'modules/auth/thunk';
import {
  getLoginData,
  hasLocationIdError as hasLocationIdErrorSelector,
  hasRegistrationError as hasRegistrationErrorSelector,
} from 'modules/auth/selectors';
import {
  getConfidentialityPolicyLink,
  getLegalNotice,
  isLoadingLogin,
} from 'modules/selfServices/selectors';
import {
  getConnectionTypes,
  getKioskName,
  getWorkflowsForAllDealers
} from 'modules/dealers/selectors';

import { Icon, PageHeader } from 'components/ui';
import Footer from 'components/ui/PageFooter';

import LoginTerms from './LoginTerms';
import BreakDownInformation from './BreakDownInformation';
import BreakdownForm from './breakdown';
import LicensePlateInput from './common/LicensePlateInput';
import KioskLocationIdInput from './KioskLocationIdInput';
import { getLabel } from './utils';
import type { LoginCardLayoutProps } from './LoginCardLayout';

type LoginFormLayoutProps = Pick<PageBaseProps, 'onPrev'> & LoginCardLayoutProps;

const LoginFormLayout: React.FC<LoginFormLayoutProps> = ({ onLoginSuccess, onPrev, onLoginError }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search, state: pageState } = useLocation();

  const loginData = useSelector(getLoginData);
  const kioskName = useSelector(getKioskName);
  const isLoading = useSelector(isLoadingLogin);
  const legalNotice = useSelector(getLegalNotice);
  const connectionTypes = useSelector(getConnectionTypes);
  const workflows = useSelector(getWorkflowsForAllDealers);
  const hasLocationIdError = useSelector(hasLocationIdErrorSelector);
  const hasRegistrationError = useSelector(hasRegistrationErrorSelector);
  const confidentialityPolicyLink = useSelector(getConfidentialityPolicyLink);

  const { registration = '', phoneNumber, locationId, dealerId, brandId, isDriver } = loginData;

  const isAtDealership = pageState?.atDealership;
  const isBreakdown = pageState?.type === SelfServiceType.BREAKDOWN;

  const [hasPhoneError, setHasPhoneError] = React.useState(false);
  const [plateNumberError, setPlateNumberError] = React.useState<string>();

  const licensePlateError = plateNumberError || hasRegistrationError;

  const disabled =
    registration.length < 1 ||
    Boolean(plateNumberError) ||
    Boolean(isBreakdown && (hasPhoneError || !phoneNumber?.local)) ||
    Boolean(isAtDealership && !locationId);

  const hasTerms = Boolean(legalNotice || confidentialityPolicyLink);
  const displayTerms = hasTerms && !connectionTypes?.includes(ConnectionTypeLogin.MFA);
  const showKioskName = isAtDealership && Boolean(kioskName);

  const loginLabelDescriptor = React.useMemo(
    () => getLabel(isBreakdown, isAtDealership),
    [isBreakdown, isAtDealership],
  );

  const handleInputPlateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const formattedRegistration = value.toUpperCase();

    dispatch(setLoginData({ ...loginData, registration: formattedRegistration }));
    setPlateNumberError(validatePlateNumber(formattedRegistration, Boolean(isBreakdown), intl));
  };

  const handleLocationIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setLoginData({ ...loginData, locationId: parseInt(value, 10) }));
  };

  const handleChangeLoginData = React.useCallback(
    (data: Partial<typeof loginData>) => {
      dispatch(setLoginData({ ...loginData, ...data }));
    },
    [dispatch, loginData],
  );

  const handleOnPrev = React.useCallback(() => {
    const hasBreakdownInstructions = isBreakdown && workflows[SelfServiceType.BREAKDOWN]?.includes(SelfServiceSteps.INSTRUCTIONS);
    if (hasBreakdownInstructions) {
      onPrev?.();
    } else {
      navigate(`/${search}`);
    }
  }, [isBreakdown, navigate, onPrev, search, workflows]);

  const handleNextClick = async () => {
    try {
      await dispatch(login({
        registration,
        connectionTypes,
        qrCode: undefined,
        type: pageState?.type,
        ...(!isBreakdown && { searchType: SelfOfficeLoginMethod.REGISTRATION }),
        locationId: isAtDealership ? locationId : undefined,
        ...(isBreakdown ? { brandId, dealerId, phoneNumber, isDriver } : {}),
      })).unwrap();
      onLoginSuccess(SelfOfficeLoginMethod.REGISTRATION);
    } catch (error) {
      onLoginError(error);
    }
  };

  const canSubmit = !disabled && !isLoading;

  return (
    <>
      <PageHeader
        hideSteps
        onPrev={handleOnPrev}
        title={<FormattedMessage id="login.title" defaultMessage="Login" />}
      />
      <div className="main-content">
        <main className="content !my-auto sm:!mt-6 mx-auto sm:!mx-0">
          {showKioskName && (
            <div className="w-full p-4 bg-card-bg rounded-card flex items-center justify-center gap-x-4 border border-brand-high">
              <div>
                <div className="flex items-center justify-center size-10 bg-brand-high rounded-full">
                  <Icon className="fb-icon--warning text-2xl mb-1 kiosk:text-2xl text-white" />
                </div>
              </div>
              <p className="text-s">
                <FormattedMessage
                  values={{ b, locationId: kioskName }}
                  id="login.existingLocationId"
                  defaultMessage="Your keys are in the kiosk {kioskName}. Please present yourself there before proceeding."
                />
              </p>
            </div>
          )}
          <div className="flex flex-col kioskSharebox:flex-row kioskSharebox:gap-8 kioskSharebox:items-center w-full">
            <div className="flex flex-col kioskSharebox:flex-1 login-content">
              <p className="text-center"><FormattedMessage {...loginLabelDescriptor} /></p>
              {isBreakdown && <BreakdownForm handleOnError={setHasPhoneError} />}
              <div>
                <LicensePlateInput
                  value={registration}
                  center={!isBreakdown}
                  canSubmit={canSubmit}
                  onSubmit={handleNextClick}
                  error={licensePlateError}
                  data-testid="plate-input"
                  onChange={handleInputPlateChange}
                  labelClassName={!isBreakdown && 'text-center'}
                />
                {isAtDealership && (
                  <KioskLocationIdInput
                    value={locationId}
                    canSubmit={canSubmit}
                    wrapperClassName="mt-5"
                    onSubmit={handleNextClick}
                    error={hasLocationIdError}
                    onChange={handleLocationIdChange}
                  />
                )}
              </div>
              {displayTerms && <LoginTerms confidentialityPolicyLink={confidentialityPolicyLink} legalNotice={legalNotice} />}
              {isBreakdown && (
                <BreakDownInformation onChange={handleChangeLoginData} dealerId={dealerId} brandId={brandId} />
              )}
            </div>
          </div>
        </main>

        <Footer
          loading={isLoading}
          disabled={disabled}
          onPrev={handleOnPrev}
          shouldDisplayBackButton
          onNext={handleNextClick}
        />
      </div>
    </>
  );
};

export default LoginFormLayout;
